import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * Subscription Context
 *
 * Provides subscription/premium status throughout the app.
 * Uses user data from AuthContext to determine tier.
 */

// Feature access configuration (mirrored from backend)
const LIMITS = {
  free: {
    maxLists: 10,
    maxPlacesPerList: 30,
    maxPinnedLists: 0,
    maxBioLinks: 0,
    maxListLinks: 0,
  },
  premium: {
    maxLists: Infinity,
    maxPlacesPerList: Infinity,
    maxPinnedLists: 5,
    maxBioLinks: 1,
    maxListLinks: 1,
  },
};

const FEATURES = {
  photos: { free: false, premium: true },
  openingHours: { free: false, premium: true },
  mapView: { free: false, premium: true },
  sortByOpen: { free: false, premium: true },
  filterByOpenAt: { free: false, premium: true },
  pinLists: { free: false, premium: true },
  reorderLists: { free: false, premium: true },
  autoFollowLists: { free: false, premium: true },
  overrideListMeta: { free: false, premium: true },
  publicSharing: { free: false, premium: true },
  bioLink: { free: false, premium: true },
  listLinks: { free: false, premium: true },
  placeSocialLinks: { free: false, premium: true },
  tripPlanner: { free: false, premium: true },
  offlineDownload: { free: false, premium: true },
  collections: { free: false, premium: true },
};

// Pricing configuration
export const PRICING = {
  monthly: {
    price: 5,
    currency: 'USD',
    period: 'month',
    priceDisplay: '$5',
  },
  annual: {
    price: 40,
    currency: 'USD',
    period: 'year',
    priceDisplay: '$40',
    savingsPercent: 33,
    monthlyEquivalent: 3.33,
  },
};

// Feature descriptions for UI
export const FEATURE_DESCRIPTIONS = {
  photos: {
    name: 'Place Photos',
    description: 'See photos of places in your lists',
  },
  openingHours: {
    name: 'Open Status',
    description: 'View real-time opening hours and business status',
  },
  mapView: {
    name: 'Map View',
    description: 'See all your places on an interactive map',
  },
  sortByOpen: {
    name: 'Sort by Open Now',
    description: 'Filter and sort places by current open status',
  },
  filterByOpenAt: {
    name: 'Find Open At',
    description: 'Search for places open at specific times and days',
  },
  pinLists: {
    name: 'Pin Lists',
    description: 'Pin up to 5 favorite lists to the top',
  },
  reorderLists: {
    name: 'Reorder Lists',
    description: 'Drag and drop to organize your lists',
  },
  autoFollowLists: {
    name: 'Auto-Follow Lists',
    description: 'Automatically follow all lists from accounts you follow',
  },
  overrideListMeta: {
    name: 'Custom Categories',
    description: 'Override list category and location',
  },
  publicSharing: {
    name: 'Public Links',
    description: 'Share lists via URL with anyone',
  },
  bioLink: {
    name: 'Bio Link',
    description: 'Add a link to your profile',
  },
  listLinks: {
    name: 'List Links',
    description: 'Add external links to your lists',
  },
  placeSocialLinks: {
    name: 'Place Social Links',
    description: 'Add links to your content about places',
  },
  tripPlanner: {
    name: 'Trip Planner',
    description: 'Turn any list into a day-by-day itinerary with drag-and-drop scheduling',
  },
  offlineDownload: {
    name: 'Offline Access',
    description: 'Download lists to access them without internet connection',
  },
  unlimitedLists: {
    name: 'Unlimited Lists',
    description: 'Create as many lists as you want',
  },
  unlimitedPlaces: {
    name: 'Unlimited Places',
    description: 'Add unlimited places to each list',
  },
};

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();

  // Helper to parse Firestore timestamps that come over JSON
  // Handles: Firestore Timestamp objects (.toDate()), serialized {_seconds} format, and ISO strings
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate(); // Firestore Timestamp object
    if (ts._seconds !== undefined) return new Date(ts._seconds * 1000); // Serialized Firestore format
    if (ts.seconds !== undefined) return new Date(ts.seconds * 1000); // Alternative serialized format
    return new Date(ts); // ISO string or other parseable format
  };

  // Check if user has active referral premium or trial
  const checkReferralPremium = (userData) => {
    if (!userData) return { hasReferralPremium: false, source: null, expiresAt: null };

    const now = new Date();

    // Check referral premium (earned from referring 3 people)
    if (userData.referralPremiumExpiresAt) {
      const expiresAt = parseTimestamp(userData.referralPremiumExpiresAt);
      if (expiresAt && expiresAt > now) {
        return { hasReferralPremium: true, source: 'referral_earned', expiresAt };
      }
    }

    // Check referral trial (2-week trial from being referred)
    if (userData.referralTrialExpiresAt) {
      const expiresAt = parseTimestamp(userData.referralTrialExpiresAt);
      if (expiresAt && expiresAt > now) {
        return { hasReferralPremium: true, source: 'referral_trial', expiresAt };
      }
    }

    return { hasReferralPremium: false, source: null, expiresAt: null };
  };

  // Derive subscription state from user data
  const subscription = useMemo(() => {
    if (!user) {
      return {
        tier: 'free',
        isPremium: false,
        status: null,
        loading: false,
        premiumSource: null,
        premiumExpiresAt: null,
        isCreator: false,
      };
    }

    // Check for creator status (lifetime premium)
    const isCreator = user.isCreator === true;

    // Check for Stripe premium status
    const hasStripePremium = user.isPremium === true || user.subscriptionTier === 'premium';

    // Check for referral-based premium
    const referralStatus = checkReferralPremium(user);

    // User has premium if creator, Stripe, or referral premium is active
    const isPremium = isCreator || hasStripePremium || referralStatus.hasReferralPremium;

    // Determine the source of premium access (creator takes priority)
    let premiumSource = null;
    let premiumExpiresAt = null;

    if (isCreator) {
      premiumSource = 'creator';
      premiumExpiresAt = null; // Lifetime - no expiration
    } else if (hasStripePremium && user.stripeSubscriptionId) {
      premiumSource = 'stripe';
      premiumExpiresAt = user.subscriptionCurrentPeriodEnd;
    } else if (referralStatus.hasReferralPremium) {
      premiumSource = referralStatus.source;
      premiumExpiresAt = referralStatus.expiresAt;
    }

    return {
      tier: isPremium ? 'premium' : 'free',
      isPremium,
      status: user.subscriptionStatus || null,
      loading: false,
      premiumSource,
      premiumExpiresAt,
      // Creator info
      isCreator,
      creatorSince: user.creatorSince || null,
      creatorFeaturedListId: user.creatorFeaturedListId || null,
      creatorFeaturedListExpiresAt: user.creatorFeaturedListExpiresAt || null,
      // Additional referral info
      hasReferralTrial: referralStatus.source === 'referral_trial',
      hasReferralPremium: referralStatus.source === 'referral_earned',
    };
  }, [user]);

  /**
   * Check if the current user can access a specific feature
   * @param {string} feature - The feature key from FEATURES
   * @returns {boolean}
   */
  const canAccess = (feature) => {
    const tierAccess = FEATURES[feature];
    if (!tierAccess) {
      console.warn(`Unknown feature: ${feature}`);
      return false;
    }
    return tierAccess[subscription.tier] || false;
  };

  /**
   * Get the limit for a specific limit type
   * @param {string} limitName - The limit key from LIMITS
   * @returns {number}
   */
  const getLimit = (limitName) => {
    const tierLimits = LIMITS[subscription.tier];
    if (!tierLimits) {
      return LIMITS.free[limitName] || 0;
    }
    return tierLimits[limitName] !== undefined ? tierLimits[limitName] : 0;
  };

  /**
   * Check if user is within their list creation limit
   * @param {number} currentListCount
   * @returns {{ allowed: boolean, remaining: number, limit: number }}
   */
  const checkListLimit = (currentListCount) => {
    const limit = getLimit('maxLists');
    const remaining = limit === Infinity ? Infinity : Math.max(0, limit - currentListCount);
    return {
      allowed: currentListCount < limit,
      remaining,
      limit,
    };
  };

  /**
   * Check if user can add more places to a list
   * @param {number} currentPlaceCount
   * @returns {{ allowed: boolean, remaining: number, limit: number }}
   */
  const checkPlaceLimit = (currentPlaceCount) => {
    const limit = getLimit('maxPlacesPerList');
    const remaining = limit === Infinity ? Infinity : Math.max(0, limit - currentPlaceCount);
    return {
      allowed: currentPlaceCount < limit,
      remaining,
      limit,
    };
  };

  /**
   * Get all premium features for display
   * @returns {Array<{ key: string, name: string, description: string, available: boolean }>}
   */
  const getAllFeatures = () => {
    return Object.entries(FEATURE_DESCRIPTIONS).map(([key, info]) => ({
      key,
      ...info,
      available: canAccess(key),
    }));
  };

  const value = {
    ...subscription,
    canAccess,
    getLimit,
    checkListLimit,
    checkPlaceLimit,
    getAllFeatures,
    PRICING,
    FEATURE_DESCRIPTIONS,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
